import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName, activateTool, deactivateTool } from "@/store/ui/ui-slice";
import {
  FaroTooltip,
  HelpBanner,
  SheetSaturationToggleIcon,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { OverlayToolbar } from "@/components/ui/overlay-toolbar";
import { Collapse, Stack } from "@mui/material";
import { useEffect, type JSX } from "react";
import { useFloorScaleContext } from "./floor-scale-mode-context";

/** @returns The overlay elements of this mode */
export function FloorScaleModeOverlay(): JSX.Element {
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector(selectActiveTool);

  const { shouldShowHelpBanner } = useFloorScaleContext();

  /** Activates the desaturation tool by default on initial render */
  useEffect(() => {
    dispatch(activateTool(ToolName.desaturateSheet));

    return () => {
      dispatch(deactivateTool());
    };
  }, [dispatch]);

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        height: "100%",
        width: "100%",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        // Disabling the pointer events is needed, to avoid event issues with the canvas and the other tooltip ui components in the mode.
        pointerEvents: "none",
      }}
    >
      {/* first empty column of a three column layout */}
      <div />
      <Collapse in={shouldShowHelpBanner} sx={{ mt: 1, pointerEvents: "none" }}>
        <HelpBanner>
          Draw a line with a known length to scale the area.
          <br />
          Hold <b>SHIFT</b> to lock the line horizontally or vertically.
        </HelpBanner>
      </Collapse>
      <Stack sx={{ justifyContent: "center" }}>
        <OverlayToolbar>
          <ToolGroup>
            <FaroTooltip title="Toggle desaturation of sheet" placement="left">
              <ToolButton
                aria-label="toggle desaturation of sheet"
                selected={activeTool === ToolName.desaturateSheet}
                onClick={() =>
                  activeTool === ToolName.desaturateSheet
                    ? dispatch(deactivateTool())
                    : dispatch(activateTool(ToolName.desaturateSheet))
                }
              >
                <SheetSaturationToggleIcon />
              </ToolButton>
            </FaroTooltip>
          </ToolGroup>
        </OverlayToolbar>
      </Stack>
    </Stack>
  );
}
